import { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, IconButton, CircularProgress } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';

export interface DocumentInfo {
  id: string;
  name: string;
  mimeType?: string | null;
  fileSize?: number | null;
}

function getIcon(mimeType?: string | null) {
  if (!mimeType) return <DescriptionIcon />;
  if (mimeType === 'application/pdf') return <PictureAsPdfIcon color="error" />;
  if (mimeType.startsWith('image/')) return <ImageIcon color="primary" />;
  return <DescriptionIcon color="action" />;
}

function formatSize(bytes?: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

async function fetchDocUrl(docId: string, disposition: 'inline' | 'attachment'): Promise<string> {
  const res = await fetch(`/api/documents/${docId}/url?disposition=${disposition}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to get document URL');
  const data = await res.json() as { url: string };
  return data.url;
}

export default function DocumentCard({ doc }: { doc: DocumentInfo }) {
  const [viewUrl, setViewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDocUrl(doc.id, 'inline')
      .then(setViewUrl)
      .catch(() => setViewUrl(null))
      .finally(() => setLoading(false));
  }, [doc.id]);

  const handleDownload = async () => {
    try {
      const url = await fetchDocUrl(doc.id, 'attachment');
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name;
      a.click();
    } catch {
      // ignore
    }
  };

  return (
    <Card variant="outlined" sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 1, gap: 1.5, maxWidth: 380 }}>
      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
        {getIcon(doc.mimeType)}
      </Box>
      <CardContent sx={{ flex: 1, p: '0 !important', overflow: 'hidden' }}>
        <Typography variant="body2" fontWeight={600} noWrap title={doc.name}>
          {doc.name}
        </Typography>
        {doc.fileSize && (
          <Typography variant="caption" color="text.secondary">
            {formatSize(doc.fileSize)}
            {doc.mimeType && ` · ${doc.mimeType.split('/')[1]?.toUpperCase()}`}
          </Typography>
        )}
      </CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
        {loading ? (
          <CircularProgress size={18} />
        ) : (
          <>
            {viewUrl && (
              <IconButton size="small" href={viewUrl} target="_blank" rel="noopener" title="Open">
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton size="small" onClick={handleDownload} title="Download">
              <DownloadIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Box>
    </Card>
  );
}
